import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Status } from '@alpha-recycling/component-library';
import { ColumnDef } from '@tanstack/react-table';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { withAlphamartIntlProvider } from 'components/shared/AlphamartIntlProvider';
import { ActionsButton } from 'components/shared/Buttons/ActionsButton';
import { ContextMenu } from 'components/shared/ContextMenu';
import { DownloadLogsModal } from 'components/shared/DownloadLogsModal/DownloadLogsModal';
import { HighlightSearch, MobileStatus, MobileStatusWrapper } from 'components/shared/List';
import { DataList } from 'components/shared/List/DataList';
import { LoadableContent } from 'components/shared/Loader';
import { PanelContent } from 'components/shared/PanelContent/PanelContent';
import { PanelContentMain } from 'components/shared/PanelContentMain/PanelContentMain';
import { PanelContentSidebar } from 'components/shared/PanelContentSidebar/PanelContentSidebar';
import { PanelHeader } from 'components/shared/PanelHeader/PanelHeader';
import { PanelLayout } from 'components/shared/PanelLayout/PanelLayout';
import { SearchBar } from 'components/shared/SearchBar/SearchBar';
import { TagGroup } from 'components/shared/TagGroup';
import { ThresholdsModal } from 'components/shared/ThresholdsModal';
import { DownloadUserShoppingCartLogsModal } from 'components/views/Companies/DownloadUserShoppingCartLogsModal/DownloadUserShoppingCartLogsModal';
import { formatDate } from 'helpers/dateTime/dateTime';
import { getUserTypeLang } from 'helpers/userTypes/userTypes';
import { FilterableModules, PERMISSIONS, UserTypes } from 'shared/constants';
import { resolveStatusColor, useAuthorization } from 'shared/helpers';
import { useAppTable, useMediaQuery, useUserActions } from 'shared/hooks';
import { useAlphamartNavigate } from 'shared/hooks/useAlphamartRouter';
import { useLanguage } from 'shared/hooks/useLanguage';
import { ItemAction, ThresholdsType, UserListItem } from 'shared/types';
import { downloadFile } from 'store/downloadFileSlice';
import { changePageIndexAction, changePagination } from 'store/listComponentSlice';
import { saveFilters } from 'store/shared/filtersSlice';
import { useAppDispatch, useAppSelector } from 'store/shared/hooks';
import { snackBarPushFailure } from 'store/shared/snackBarSlice';
import { clearUser, fetchUser } from 'store/userDetailsSlice';
import { fetchUsers } from 'store/usersListSlice';
import { MEDIA_QUERY, theme } from 'theme';
import { UsersFilters } from './UsersFilters';
import { useUserItemActions } from '../hooks/useUserItemActions';
import {
  messages,
  TypedFormattedMessage as FormattedMessage,
  UsersMessages,
  useTypedIntl,
} from '../locale/messages';
import { UserDetails } from '../UserDetails/UserDetails';

const UsersListComponent = (): React.ReactElement => {
  const authorize = useAuthorization();
  const navigate = useAlphamartNavigate();
  const dispatch = useAppDispatch();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const intl = useTypedIntl();
  const listComponent = useAppSelector(state => state.listComponent);
  const users = useAppSelector(state => state.users);
  const userDetails = useAppSelector(state => state.userDetails);
  const savedFilters = useAppSelector(state => state.filters[FilterableModules.USERS]);
  const { id } = useParams<{ id: string }>();
  const contentRef = useRef(null);
  const language = useLanguage();
  const isMd = useMediaQuery(MEDIA_QUERY.MD);
  const isLg = useMediaQuery(MEDIA_QUERY.LG);
  const searchWords = savedFilters.data.query ? [savedFilters.data.query] : [];

  const tableColumns: ColumnDef<UserListItem>[] = [
    {
      id: 'status',
      header: intl.formatMessage({ id: 'UsersList.TableHeader.Status' }),
      cell: ({ row }) => (
        <Status
          type={resolveStatusColor(row.original.status)}
          value={intl.formatMessage({
            id: `Global.Status.${row.original.status}` as keyof UsersMessages,
          })}
          size="big"
        />
      ),
    },
    {
      id: 'userTypes',
      header: intl.formatMessage({ id: 'UsersList.TableHeader.UserTypes' }),
      cell: ({ row }) => (
        <MobileStatusWrapper>
          {!isLg && <MobileStatus theme={theme} state={row.original.status.toLowerCase()} />}
          <TagGroup
            items={row.original.userTypes.map(({ name }) => getUserTypeLang(name, intl))}
            countLabelFormatter={count => intl.formatMessage({ id: 'Global.Roles' }, { count })}
          />
        </MobileStatusWrapper>
      ),
    },
    {
      id: 'name',
      header: intl.formatMessage({ id: 'UsersList.TableHeader.Name' }),
      cell: ({ row }) => (
        <HighlightSearch
          autoEscape
          searchWords={searchWords}
          textToHighlight={`${row.original.firstName} ${row.original.lastName}`}
        />
      ),
    },
    {
      id: 'companyName',
      header: intl.formatMessage({ id: 'UsersList.TableHeader.CompanyName' }),
      cell: ({ row }) => (
        <HighlightSearch
          autoEscape
          searchWords={searchWords}
          textToHighlight={row.original.company.name}
        />
      ),
    },
    {
      id: 'email',
      header: intl.formatMessage({ id: 'UsersList.TableHeader.Email' }),
      cell: ({ row }) => (
        <HighlightSearch
          autoEscape
          searchWords={searchWords}
          textToHighlight={row.original.email}
        />
      ),
    },
    {
      id: 'totalMargin',
      header: intl.formatMessage({ id: 'UsersList.TableHeader.TotalMargin' }),
      cell: ({ row }) =>
        row.original.company.canUseProfitMarginPerMetal
          ? FormattedMessage({
              id: 'UsersList.TotalProfitMarginPdPtRh',
              values: {
                pd: row.original.totalMarginPd,
                pt: row.original.totalMarginPt,
                rh: row.original.totalMarginRh,
              },
            })
          : `${row.original.totalMarginPt}%`,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <ContextMenu
          offsetX={-60}
          trigger={
            <Button data-cy="actionButton" iconName="ThreeDots" variant="plain" content="icon" />
          }
        >
          {actions.map(
            action =>
              (typeof action.visible === 'function'
                ? (action.visible as (item?: UserListItem | undefined) => boolean)(row.original)
                : action.visible) && (
                <button
                  type="button"
                  onClick={() => action.onClick(row.original)}
                  key={action.id}
                  data-cy={action.dataCy?.(row.original)}
                >
                  {action.label}
                </button>
              ),
          )}
        </ContextMenu>
      ),
    },
  ];

  const onFiltersToggle = useCallback(() => setFiltersOpen(!filtersOpen), [filtersOpen]);

  useDeepCompareEffect(() => {
    dispatch(
      fetchUsers({
        ...savedFilters.data,
        page: listComponent.pageIndex + 1,
        pageSize: listComponent.pageSize,
      }),
    );
  }, [listComponent.pageIndex, listComponent.pageSize, savedFilters, dispatch]);

  useEffect(() => {
    dispatch(id ? fetchUser(+id) : clearUser());
  }, [id, dispatch]);

  const usersList = useMemo(
    () =>
      users.list.map(user => ({
        color: !isMd ? resolveStatusColor(user.status) : '',
        ...user,
      })),
    [isMd, users.list],
  );

  const setPageIndex = currentPage => dispatch(changePageIndexAction(currentPage));

  const onFiltersChanged = newFilters => {
    dispatch(
      saveFilters({
        filterKey: FilterableModules.USERS,
        values: newFilters,
      }),
    );
    setPageIndex(0);
  };
  const goToUser = item => {
    setFiltersOpen(false);
    navigate(`/users/${item.id}`);
  };
  const { isPending: areUserActionsPending } = useUserActions();

  const loaderEnabled =
    (!users.list && !userDetails.isPending && !userDetails.user) || areUserActionsPending;

  const actions = useUserItemActions();

  const handleGenerateBtnClick = async () => {
    try {
      await dispatch(
        downloadFile(
          '/api/users/report',
          `${intl.formatMessage({ id: 'UsersList.ReportName' })} ${formatDate(
            new Date(),
            language === 'EN' ? 'MM-dd-yyyy' : 'dd-MM-yyyy',
          )}.xlsx`,
        ),
      );
    } catch (error) {
      dispatch(snackBarPushFailure(intl.formatMessage({ id: 'Global.Error.SomethingWentWrong' })));
    }
  };

  const buttonActions: ItemAction[] = [
    {
      id: 1,
      label: <FormattedMessage id="UsersList.Display.GenerateReport" />,
      onClick: handleGenerateBtnClick,
      dataCy: () => 'users-list-display-generate-report',
      visible: authorize([UserTypes.SUPER_ADMIN, UserTypes.CUSTOMER_SUPPORT]),
    },
  ];

  const table = useAppTable({
    columns: tableColumns,
    data: usersList,
    pageCount: Math.ceil(users.count / listComponent.pageSize),
    onPaginationChange: pagination => dispatch(changePagination(pagination)),
    manualPagination: true,
    state: {
      columnVisibility: {
        status: isLg,
        state: isLg,
        companyName: authorize(PERMISSIONS.USERS.LIST_COMPANY_NAME) && isMd,
        email: isLg,
        totalMargin: isLg,
      },
      pagination: { pageIndex: listComponent.pageIndex, pageSize: listComponent.pageSize },
    },
  });

  return (
    <>
      <PanelLayout>
        <PanelHeader title={<FormattedMessage id="UsersList.Header.Users" />}>
          <ActionsButton actions={buttonActions} />
        </PanelHeader>
        <PanelContent>
          <PanelContentMain disableScroll={loaderEnabled} ref={contentRef}>
            <SearchBar
              isSearchPending={users.isPending}
              onSearchChanged={onFiltersChanged}
              initialQuery={savedFilters.data.query}
              onFiltersToggle={onFiltersToggle}
              savedFilters={savedFilters}
            />
            <DataList
              table={table}
              isLoading={users.isPending || areUserActionsPending}
              onRowClicked={goToUser}
              restIndex={isMd ? 2 : 1}
            />
          </PanelContentMain>
          <PanelContentSidebar
            header={intl.formatMessage({ id: 'Global.Filters' })}
            headerIcon="Filter"
            open={filtersOpen}
            onSidebarClosed={onFiltersToggle}
          >
            <UsersFilters onFiltersChanged={onFiltersChanged} onFiltersApplied={onFiltersToggle} />
          </PanelContentSidebar>
        </PanelContent>
        <DownloadUserShoppingCartLogsModal />
        <DownloadLogsModal />
        <LoadableContent loading={userDetails.isPending}>
          <UserDetails user={userDetails.user} />
        </LoadableContent>
      </PanelLayout>
      <ThresholdsModal modalEndpoint={ThresholdsType.USER} />
    </>
  );
};

export const UsersList = withAlphamartIntlProvider(UsersListComponent, messages);
